import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { PersonService } from '@features/people/services/person.service';
import { PersonLookupOption } from '@features/people/models/person-event.model';
import { MemberLinkSourceComponent } from '@shared/components/member-link-source/member-link-source.component';

@Component({
  selector: 'app-member-association-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatFormFieldModule, MatInputModule, MatIconModule, MemberLinkSourceComponent],
  template: `
    <div class="member-association">
      <div class="member-association__value">
        <ng-content></ng-content>
        <ng-container *ngIf="associate">
          <mat-form-field appearance="fill" floatLabel="always"><input matInput aria-label="Buscar miembro" placeholder="" [ngModel]="personName" (ngModelChange)="search($event)" [matAutocomplete]="people">
            <mat-autocomplete #people="matAutocomplete"><mat-option *ngFor="let option of options" [value]="option.name" (onSelectionChange)="$event.isUserInput && select(option)">{{ option.name }}</mat-option></mat-autocomplete>
          </mat-form-field>
          <small *ngIf="!personId">Seleccioná un miembro de Comunidades.</small>
          <app-member-link-source *ngIf="personId" [personId]="personId"></app-member-link-source>
        </ng-container>
      </div>
      <button type="button" class="member-association__toggle" [class.member-association__toggle--active]="associate" [attr.aria-pressed]="associate" [attr.aria-label]="associate ? 'Quitar asociación con un miembro' : 'Asociar a un miembro'" [attr.title]="associate ? 'Quitar asociación' : 'Asociar a un miembro'" (click)="toggle()"><mat-icon>{{ associate ? 'link_off' : 'person_add' }}</mat-icon></button>
    </div>`,
  styles: [`
    .member-association { display: flex; align-items: center; gap: .45rem; width: 100%; min-width: 0; }
    .member-association__value { flex: 1 1 auto; min-width: 0; }
    .member-association__toggle { display: inline-flex; flex: 0 0 30px; align-items: center; justify-content: center; width: 30px; height: 30px; margin: 0; padding: 0; border: 1px solid #9bbbd1; border-radius: 50%; color: #174d78; background: #fff; cursor: pointer; }
    .member-association__toggle--active { border-color: #2b7bbb; color: #174d78; background: #eaf5fc; }
    .member-association__toggle mat-icon { width: 18px; height: 18px; font-size: 18px; line-height: 18px; }
    .member-association__toggle:focus-visible { outline: 2px solid #2b7bbb; outline-offset: 2px; }
    mat-form-field { --mat-form-field-container-height: 36px; --mat-form-field-container-vertical-padding: 7px; display: block; box-sizing: border-box; width: 100%; height: 36px; margin: 0; }
    small { display: block; margin-top: 0; color: #b3261e; }
    :host ::ng-deep .mat-mdc-text-field-wrapper { height: 36px; padding: 0; background: transparent; }
    :host ::ng-deep .mat-mdc-form-field-flex { height: 36px; align-items: center; }
    :host ::ng-deep .mat-mdc-form-field-infix { box-sizing: border-box; height: 36px; min-height: 0; padding: .35rem 0; }
    :host ::ng-deep .mat-mdc-form-field-subscript-wrapper { display: none; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberAssociationSelectorComponent {
  @Input() associate = false;
  @Output() associateChange = new EventEmitter<boolean>();
  @Input() personId?: string;
  @Output() personIdChange = new EventEmitter<string | undefined>();
  @Input() personName?: string;
  @Output() personNameChange = new EventEmitter<string | undefined>();
  options: PersonLookupOption[] = [];
  private people = inject(PersonService);
  private cdr = inject(ChangeDetectorRef);

  toggle(): void {
    this.associate = !this.associate;
    this.associateChange.emit(this.associate);
    if (!this.associate) {
      this.personId = undefined;
      this.personName = undefined;
      this.personIdChange.emit(undefined);
      this.personNameChange.emit(undefined);
      this.options = [];
    }
  }

  search(term: string): void {
    this.personName = term;
    this.personNameChange.emit(term);
    this.personId = undefined;
    this.personIdChange.emit(undefined);
    if (term.trim().length < 2) { this.options = []; return; }
    this.people.searchPeople(term).subscribe(items => {
      if (this.personName !== term) return;
      this.options = items;
      this.cdr.markForCheck();
    });
  }

  select(person: PersonLookupOption): void {
    this.personName = person.name;
    this.personNameChange.emit(person.name);
    this.personId = person.id;
    this.personIdChange.emit(person.id);
    this.options = [];
  }
}
